<template>
  <FadeTransition>
    <div v-if="viewer">
      <AccountEditorTable
        :email="viewer.staff.email"
        :isEditing="isEditing === 'Password'"
        :isButtonDisabled="isSending === 'Password'"
        @edit="isEditing = 'Password'"
        @save="handlePasswordSave"
        @cancel="isEditing = undefined"
        :class="$style.editorTable"
      />
      <AccountProfileEditorTable
        :staffFragment="viewer.staff"
        :isEditing="isEditing === 'Profile'"
        :isButtonDisabled="isSending === 'Profile'"
        @edit="isEditing = 'Profile'"
        @save="handleProfileSave"
        @cancel="isEditing = undefined"
        :class="$style.editorTable"
      />
    </div>
    <div v-else :class="$style.loader">
      <LoadingIndicator />
    </div>
  </FadeTransition>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import axios from 'axios'
import { useHead } from '@unhead/vue'
import { useRoute, onBeforeRouteLeave } from 'vue-router'
import router from '@/router'
import { useAlert } from '@/libs/alertPortal'
import { isClientError } from '@/libs/utilities/CustomErrors'

import FadeTransition from '@/components/transitions/FadeTransition.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'

import { captureException as newrelicCaptureException } from '@/newrelic'
import AccountEditorTable, {
  type PasswordFormValues,
} from './AccountEditorTable.vue'
import AccountProfileEditorTable, {
  type ProfileFormValues,
} from './AccountProfileEditorTable.vue'
import {
  useChangePasswordWithLoggedInMutation,
  useSettingsPageAccountViewQuery,
  useIssueStaffAvatarImageUploadUrlMutation,
  useUpdateStaffProfileMutation,
} from './SettingsPageAccountView.generated'

const { result, refetch } = useSettingsPageAccountViewQuery({})

const viewer = computed(() => result.value?.viewer)

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const alert = useAlert()
const route = useRoute()

type AccountFormType = 'Password' | 'Profile'

const isSending = ref<Maybe<AccountFormType>>()
const isCompleted = ref<Maybe<AccountFormType>>()
const isEditing = computed({
  get(): Maybe<AccountFormType> {
    return route.name === 'accountSettingsPasswordEditing'
      ? 'Password'
      : route.name === 'accountSettingsProfileEditing'
        ? 'Profile'
        : undefined
  },
  set(value: Maybe<AccountFormType>): void {
    const name =
      value === 'Password'
        ? 'accountSettingsPasswordEditing'
        : value === 'Profile'
          ? 'accountSettingsProfileEditing'
          : 'accountSettings'
    // eslint-disable-next-line fp/no-mutating-methods
    router.push({ name })
  },
})

useHead({
  title: () => (isEditing.value ? '編集 | アカウント設定' : 'アカウント設定'),
})

const issueStaffAvatarImageUploadUrlMutation =
  useIssueStaffAvatarImageUploadUrlMutation()

const updateStaffProfileMutation = useUpdateStaffProfileMutation()

const changePasswordWithLoggedInMutation =
  useChangePasswordWithLoggedInMutation()

const handlePasswordSave = async (value: PasswordFormValues): Promise<void> => {
  isSending.value = 'Password'
  try {
    // パスワード更新
    // 他のプロフィールと切り離しやすいように個別のエンドポイントにしておく
    const mutationResult = await changePasswordWithLoggedInMutation.mutate({
      password: value.password,
    })
    if (mutationResult?.data == null) {
      throw new Error('changePasswordWithLoggedInMutation failed')
    }
    if (!mutationResult.data.changePasswordWithLoggedIn.success) {
      throw new Error('changePasswordWithLoggedInMutation failed')
    }

    isCompleted.value = 'Password'
    isEditing.value = undefined
    alert.success('入力した内容が変更されました。')
  } catch (error) {
    console.log(error)
    alert.error('更新処理中にエラーが発生しました。')

    if (!isClientError(error)) {
      newrelicCaptureException(error)
    }
  }
  isSending.value = undefined
}

const handleProfileSave = async (value: ProfileFormValues): Promise<void> => {
  isSending.value = 'Profile'
  try {
    const currentImageName = viewer.value?.staff.profile.avatar
      ? viewer.value?.staff.profile.avatar.split('/')
      : undefined
    // 新しく画像が設定されていたらそれをアップロードして名前を取得する
    // 画像が設定されていない場合は現在の画像名をそのまま使う
    const avatarImageName = value.avatarImage
      ? await uploadImage(value.avatarImage)
      : currentImageName
        ? currentImageName[currentImageName.length - 1]
        : undefined

    await updateStaffProfileMutation.mutate({
      input: {
        avatarImageName: avatarImageName,
        name: value.name,
      },
    })

    isCompleted.value = 'Profile'
    isEditing.value = undefined
    alert.success('入力した内容が変更されました。')
  } catch (error) {
    console.log(error)
    alert.error('更新処理中にエラーが発生しました。')

    if (!isClientError(error)) {
      newrelicCaptureException(error)
    }
  }
  isSending.value = undefined
  refetch()
}

const uploadImage = async (file: File): Promise<string> => {
  const res = await issueStaffAvatarImageUploadUrlMutation.mutate()
  const uploadURL = res?.data?.issueStaffAvatarImageUploadUrl.uploadURL
  if (!uploadURL) {
    throw new Error('画像のアップロードに失敗しました。')
  }
  await axios.put(uploadURL, file, {
    headers: {
      'Content-Type': file.type,
    },
  })

  const paths = URL.parse(uploadURL)?.pathname?.split('/')
  if (!paths) {
    throw new Error('画像のアップロードに失敗しました。')
  }
  return paths[paths.length - 1]
}

onBeforeRouteLeave((to, from, next) => {
  if (from.name === 'accountSettings') {
    next()
    return
  }
  if (to.name === 'accountSettings' && isEditing.value) {
    isEditing.value = undefined
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value =
        from.name === 'accountSettingsPasswordEditing' ? 'Password' : 'Profile'
      next()
    },
    onAbort: () => next(false),
  })
})
</script>

<style lang="scss" module>
.loader {
  display: flex;
  height: 560px;
  align-items: center;
  justify-content: center;
}

.info {
  display: flex;
  align-items: center;

  &__icon {
    color: $color-text-and-object-accent-primary;
    font-size: 14px;
  }

  &__text {
    color: $color-text-and-object-medium-emphasis;
    font-size: 12px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.editorTable {
  &:nth-child(n + 2) {
    margin-top: 24px;
  }
}
</style>
